import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { X, Check } from "lucide-react";
import { createBooking, catalogUrl } from "@/lib/api";

const TIME_SLOTS = ["09:00", "10:30", "12:00", "14:00", "15:30", "17:00"];

const inputCls = "field";
const labelCls = "eyebrow !text-[0.6rem] text-smoke block mb-1 mt-6";

export default function BookingDialog({ service, onClose }) {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", notes: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  if (!service) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) {
      setError("Please choose a date and a time.");
      return;
    }
    setState("sending");
    setError("");
    try {
      await createBooking({
        service: service.key,
        name: form.name,
        email: form.email,
        company: form.company,
        date: date.toISOString().slice(0, 10),
        time_slot: slot,
        notes: form.notes,
      });
      setState("done");
    } catch (err) {
      setState("idle");
      setError("Something went wrong sending your request. Please try again.");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="booking-dialog"
        className="fixed inset-0 z-[70] flex items-end md:items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="absolute inset-0 bg-ink/45 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative bg-ivory w-full md:max-w-4xl max-h-[92vh] overflow-y-auto border border-line"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            data-testid="booking-close-button"
            onClick={onClose}
            aria-label="Close booking"
            className="absolute top-5 right-5 p-2 text-smoke hover:text-ink transition-colors duration-300 z-10"
          >
            <X size={20} strokeWidth={1.5} />
          </button>

          {state === "done" ? (
            <div className="px-8 md:px-16 py-24 text-center" data-testid="booking-confirmation">
              <span className="inline-flex items-center justify-center w-12 h-12 border border-green text-green mb-8">
                <Check size={20} strokeWidth={1.5} />
              </span>
              <p className="eyebrow text-honey mb-5">{service.name} — Request received</p>
              <h3 className="font-serif text-4xl md:text-5xl leading-tight">Thank you.</h3>
              <p className="mt-6 text-smoke max-w-md mx-auto leading-relaxed">
                We'll be in touch within one business day.
                {service.key === "catalog_pdf" && (
                  <> Your catalogue is also ready now —{" "}
                    <a
                      href={catalogUrl}
                      data-testid="booking-catalog-download"
                      className="underline underline-offset-4 decoration-honey hover:text-honey transition-colors duration-300"
                    >
                      download the PDF
                    </a>.
                  </>
                )}
              </p>
              <button
                data-testid="booking-done-close"
                onClick={onClose}
                className="eyebrow !text-[0.625rem] mt-10 border border-ink px-8 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300"
              >
                Return to services
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2">
              <div className="bg-cream border-b md:border-b-0 md:border-r border-line px-8 md:px-10 py-12">
                <p className="eyebrow text-honey mb-4">Book a call — 15 min · Free</p>
                <h3 className="font-serif text-3xl leading-tight mb-8">{service.name}</h3>
                <div data-testid="booking-calendar" className="inline-block bg-ivory border border-line p-4">
                  <DayPicker
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={{ before: new Date() }}
                    weekStartsOn={1}
                  />
                </div>
                <p className="eyebrow !text-[0.6rem] text-smoke mt-8 mb-3">Available times (GMT)</p>
                <div className="flex flex-wrap gap-2" data-testid="booking-time-slots">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`booking-slot-${t.replace(":", "")}`}
                      onClick={() => setSlot(t)}
                      className={`font-mono text-xs px-4 py-2.5 border transition-colors duration-300 ${
                        slot === t
                          ? "bg-green text-ivory border-green"
                          : "border-line hover:border-ink"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={submit} className="px-8 md:px-10 py-12" data-testid="booking-form">
                <label className={labelCls}>Full name *</label>
                <input
                  data-testid="booking-name-input"
                  className={inputCls}
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                />
                <label className={labelCls}>Email *</label>
                <input
                  data-testid="booking-email-input"
                  className={inputCls}
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@company.co.uk"
                />
                <label className={labelCls}>Company name</label>
                <input
                  data-testid="booking-company-input"
                  className={inputCls}
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Optional"
                />
                <label className={labelCls}>Notes</label>
                <textarea
                  data-testid="booking-notes-input"
                  className={`${inputCls} resize-none`}
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anything we should know before the call"
                />
                {error && (
                  <p data-testid="booking-error" className="mt-6 text-sm text-honey italic font-serif">
                    {error}
                  </p>
                )}
                <button
                  data-testid="booking-submit-button"
                  type="submit"
                  disabled={state === "sending"}
                  className="eyebrow !text-[0.625rem] w-full mt-10 bg-green text-ivory py-5 hover:bg-ink transition-colors duration-300 disabled:opacity-50"
                >
                  {state === "sending" ? "Sending…" : "Confirm booking"}
                </button>
              </form>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
